import * as React from "react";
import { RLink } from "./RLink";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

type ButtonProps = {
  onClick?: () => void;
  children: React.ReactNode;
  to?: string;
  variant?: "default" | "primary" | "admin" | "success" | "text" | "danger";
  disabled?: boolean;
  icon?: React.ReactNode;
  grow?: boolean;
  isLoading?: boolean;
  // allow arbitrary data-* attrs for testing
  [key: `data-${string}`]: unknown;
};

export const Button = ({
  onClick,
  children,
  to,
  variant = "default",
  disabled = false,
  icon,
  grow = true,
  isLoading = false,
  ...rest
}: ButtonProps): JSX.Element => {
  const baseClass =
    "flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-center text-sm font-medium transition cursor-pointer";
  const growClass = grow ? "flex-1" : "";

  const variantClasses = {
    default:
      "border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-white dark:border-slate-700 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 dark:hover:text-white",
    primary:
      "bg-amber-500 text-white hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-500",
    admin:
      "border border-violet-300/50 bg-violet-50 text-violet-700 hover:bg-violet-100 hover:text-white dark:border-violet-500/30 dark:bg-violet-900/20 dark:text-violet-400 dark:hover:bg-violet-900/50",
    success:
      "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 hover:text-white dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50",
    text: "bg-transparent text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700",
    danger:
      "bg-red-600 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600",
  };

  const isDisabled = disabled || isLoading;
  const disabledClass = isDisabled
    ? "opacity-50 cursor-not-allowed pointer-events-none"
    : "";

  const combinedClass = `${baseClass} ${variantClasses[variant]} ${disabledClass}`;

  const content = (
    <>
      {icon}
      {children}
      {isLoading && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
    </>
  );

  if (to) {
    return (
      <RLink to={to} variant="unstyled" className={growClass} {...rest}>
        <div className={combinedClass}>{content}</div>
      </RLink>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={`${combinedClass} ${growClass}`}
      {...rest}
    >
      {content}
    </button>
  );
};
